// Sugalarm — Household dashboard hero card (cycle 14).
//
// Shows the active member's avatar, display name + provider sub-line,
// and the live big number with trend arrow on the right. Mirrors the
// design's "Active card" — full width above the "Also in this
// household" caption.

import { TrendArrow } from "../../components/glucose/TrendArrow";
import { MemberAvatar } from "../../components/ui/MemberAvatar";
import { Pill } from "../../components/ui/Pill";
import type { CurrentGlucoseView } from "../../lib/currentGlucose";
import {
  displayValue,
  fmtAge,
  trendInfo,
  zoneLabel,
  zoneOf,
  zoneTone,
} from "../../lib/glucose";
import { followerStoppedLine } from "../../lib/serviceLock";
import type { AlertLevel, Thresholds, Zone } from "../../lib/types";
import styles from "./HeroMemberCard.module.css";

interface Props {
  displayName: string;
  /** Cycle 105: member avatar id (null → Sugalarm shield). */
  avatarId?: string | null;
  relationshipLabel: string | null;
  isSelf: boolean;
  cgmProvider: string | null;
  /** ⚠ Cycle 435: the card receives the selector's RESULT for this member
   *  (`lib/currentGlucose.ts`) and paints a number only from
   *  `view.displayValue`. It used to take a raw `Reading` plus a transport
   *  flag and grey a stale value while still painting it; a greyed number
   *  is still a number a caregiver reads. The result carries the sample's
   *  age for the label and its status for the pill copy. */
  view: CurrentGlucoseView;
  /** Cycle 435 (regression-guard): the server's own alert level for the
   *  member, from the dashboard seed. When the number is withheld the pill
   *  keeps its tone and label from THIS — server truth — rather than from
   *  the stale sample's zone as it did before, and rather than collapsing
   *  to a neutral "—" as the first draft of this cycle did. */
  currentAlertLevel?: AlertLevel | null;
  units: "mg/dL" | "mmol/L";
  /** Cycle 35: live thresholds threaded from HouseholdScreen via
   *  `useThresholds()`. Required so zone mapping respects the user's
   *  configured low/high boundaries. */
  thresholds: Thresholds;
  /** Cycle 169: frosted-glass fill — set only on the mobile frame so the
   *  aurora glows through. Kiosk/web omit it and keep the solid card. */
  glass?: boolean;
  /** Cycle 468: the member's owner is locked out (the server's
   *  `service_state` / `service_locked`). The card shows the stopped line in
   *  place of readings and No Data; the number is withheld. */
  serviceLocked?: boolean;
}

function providerLabel(provider: string | null): string {
  if (!provider) return "Not connected";
  if (provider === "dexcom") return "Dexcom";
  if (provider === "libre") return "Libre";
  return provider[0]!.toUpperCase() + provider.slice(1);
}

export function HeroMemberCard({
  displayName,
  avatarId = null,
  relationshipLabel,
  isSelf,
  cgmProvider,
  view,
  currentAlertLevel = null,
  units,
  thresholds,
  glass = false,
  serviceLocked = false,
}: Props) {
  // Cycle 435: one fact from the selector — released as current, or not.
  // The server's verdict, the sample's age in seconds and the per-member
  // stream confirmation are all folded upstream; the card's existing No
  // Data treatment is reused, with the number withheld rather than greyed.
  const current = view.displayValue;
  const hasReading = view.reading != null;
  const stale = current == null;
  // The pill's zone: the current value's zone, or — when the number is
  // withheld — the SERVER's own alert level for the member, so an open
  // urgent low keeps its urgent tone on the card.
  const zone: Zone = current
    ? zoneOf(current.sgv, thresholds)
    : (currentAlertLevel ?? "unknown");
  const ageMin = view.ageMinutes ?? 0;
  const trend = trendInfo(current?.trend ?? null, current?.trendRate ?? null);
  const tone = zoneTone(zone);

  return (
    <div className={`${styles.hero} ${glass ? styles.glass : ""}`}>
      <div className={styles.left}>
        <MemberAvatar avatarId={avatarId} name={displayName} size={56} />
        <div className={styles.meta}>
          <div className={styles.name}>{displayName}</div>
          <div className={styles.sub}>
            <span className={styles.relLabel}>
              {isSelf ? "YOU" : (relationshipLabel || "MEMBER").toUpperCase()}
            </span>
            <span className={styles.dot} />
            <span>{providerLabel(cgmProvider)}</span>
          </div>
          <div className={styles.pillRow}>
            {/* Cycle 162.1: explicit "this is the active member" pill —
                the hero card always shows the active member, but nothing
                said so. Accent-tinted (color-mix on fill, never element
                opacity) so it reads apart from the zone pill. */}
            <span className={styles.activePill}>
              <span className={styles.activeDot} aria-hidden />
              Active
            </span>
            <Pill tone={serviceLocked ? "neutral" : tone} appearance="status">
              {serviceLocked
                ? "Monitoring stopped"
                : !hasReading
                  ? "No reading yet"
                  : stale
                    ? "No recent reading"
                    : zoneLabel(zone)}
            </Pill>
            {hasReading && !serviceLocked && (
              <span className={styles.updatedLabel}>
                UPDATED {fmtAge(ageMin)}
              </span>
            )}
          </div>
          {serviceLocked ? (
            <p className={styles.stopped} data-testid="member-stopped-line">
              {followerStoppedLine(displayName)}
            </p>
          ) : null}
        </div>
      </div>

      <div className={styles.right}>
        <div className={styles.bigValue} style={{ opacity: stale ? 0.55 : 1 }}>
          {current && !serviceLocked ? displayValue(current.sgv, units) : "—"}
        </div>
        <div className={styles.bigUnit}>{units}</div>
        <div className={styles.trend}>
          <TrendArrow
            glyph={current ? trend.glyph : "—"}
            zone={zone}
            stale={stale}
            size={42}
          />
        </div>
      </div>
    </div>
  );
}
